import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token storage — sessionStorage for Bearer auth
let _accessToken = null;
let _refreshToken = null;

try {
  _accessToken = sessionStorage.getItem("access_token");
  _refreshToken = sessionStorage.getItem("refresh_token");
} catch {}

function saveTokens(access, refresh) {
  _accessToken = access || null;
  _refreshToken = refresh || null;
  try {
    if (access) sessionStorage.setItem("access_token", access); else sessionStorage.removeItem("access_token");
    if (refresh) sessionStorage.setItem("refresh_token", refresh); else sessionStorage.removeItem("refresh_token");
  } catch {}
}

function clearTokens() {
  _accessToken = null;
  _refreshToken = null;
  try { sessionStorage.removeItem("access_token"); sessionStorage.removeItem("refresh_token"); } catch {}
}

// Axios instance — sends Bearer token AND cookies (for session_token)
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  if (_accessToken) config.headers.Authorization = `Bearer ${_accessToken}`;
  return config;
});

// Refresh interceptor
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retry && !orig.url?.includes("/auth/")) {
      orig._retry = true;
      if (!refreshing) {
        refreshing = api.post("/auth/refresh", { refresh_token: _refreshToken })
          .then((r) => { if (r.data?.access_token) saveTokens(r.data.access_token, _refreshToken); return r; })
          .catch((err) => { clearTokens(); throw err; })
          .finally(() => { refreshing = null; });
      }
      try {
        await refreshing;
        orig.headers.Authorization = `Bearer ${_accessToken}`;
        return api(orig);
      } catch { return Promise.reject(error); }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Skip auth check if returning from OAuth callback — AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!_accessToken) {
      // No JWT token — try session_token cookie via /auth/me
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(null);
      }
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Password login
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  // Set user from Google OAuth callback
  const setGoogleUser = (data) => {
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role, picture: data.picture });
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setGoogleUser, checkAuth, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { api };
